import { encodeCanvas } from '../shared/imageOutputFormats';
export const PEN_COLORS = ['#000000', '#1E3A8A', '#374151'];
export const STROKE_WIDTHS = [2, 3, 5];
export const BACKGROUND_MODES = ['transparent', 'white'];

function parseHexColor(hex) {
    const normalized = String(hex || '').trim();
    const match = /^#?([0-9a-f]{6})$/i.exec(normalized);
    if (!match) return null;
    const value = match[1];
    return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
    };
}

function extractAlphaMask(data, width, height) {
    const alpha = new Uint8Array(width * height);
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            alpha[y * width + x] = data[(y * width + x) * 4 + 3];
        }
    }
    return alpha;
}

function morphAlphaMask(alpha, width, height, radius) {
    if (!radius) return alpha;

    if (radius > 0) {
        const out = new Uint8Array(alpha.length);
        const r = Math.ceil(radius);
        for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
                let maxA = 0;
                for (let dy = -r; dy <= r; dy += 1) {
                    for (let dx = -r; dx <= r; dx += 1) {
                        if (dx * dx + dy * dy > radius * radius) continue;
                        const nx = x + dx;
                        const ny = y + dy;
                        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
                        maxA = Math.max(maxA, alpha[ny * width + nx]);
                    }
                }
                out[y * width + x] = maxA;
            }
        }
        return out;
    }

    const erodeR = Math.ceil(-radius);
    const out = new Uint8Array(alpha.length);
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            let minA = 255;
            for (let dy = -erodeR; dy <= erodeR; dy += 1) {
                for (let dx = -erodeR; dx <= erodeR; dx += 1) {
                    if (dx * dx + dy * dy > erodeR * erodeR) continue;
                    const nx = x + dx;
                    const ny = y + dy;
                    if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
                        minA = 0;
                        continue;
                    }
                    minA = Math.min(minA, alpha[ny * width + nx]);
                }
            }
            out[y * width + x] = minA;
        }
    }
    return out;
}

function paintAlphaMask(imageData, alpha, color) {
    const rgb = parseHexColor(color);
    if (!rgb) return;
    const { data } = imageData;
    for (let i = 0; i < alpha.length; i += 1) {
        const a = alpha[i];
        if (a <= 0) {
            data[i * 4 + 3] = 0;
            continue;
        }
        data[i * 4] = rgb.r;
        data[i * 4 + 1] = rgb.g;
        data[i * 4 + 2] = rgb.b;
        data[i * 4 + 3] = a;
    }
}

/** Recolor and thicken/thin existing ink on the pad when pen settings change. */
export function reapplyPenStyleToCanvas(
    canvas,
    logicalW,
    logicalH,
    dpr,
    newColor,
    newWidth,
    prevColor,
    prevWidth,
) {
    if (!canvas || logicalW <= 0 || logicalH <= 0) return false;
    if (!parseHexColor(newColor)) return false;

    const scratch = document.createElement('canvas');
    scratch.width = logicalW;
    scratch.height = logicalH;
    const scratchCtx = scratch.getContext('2d');
    if (!scratchCtx) return false;

    scratchCtx.drawImage(canvas, 0, 0, logicalW, logicalH);
    const imageData = scratchCtx.getImageData(0, 0, logicalW, logicalH);

    let alpha = extractAlphaMask(imageData.data, logicalW, logicalH);
    const hasInk = alpha.some((value) => value > 0);
    if (!hasInk) return false;

    const widthDelta = (newWidth - prevWidth) / 2;
    if (Math.abs(widthDelta) >= 0.5) {
        alpha = morphAlphaMask(alpha, logicalW, logicalH, widthDelta);
    }

    paintAlphaMask(imageData, alpha, newColor);
    scratchCtx.putImageData(imageData, 0, 0);

    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(scratch, 0, 0, logicalW, logicalH);

    return true;
}

export function outputFileName(ext = 'png') {
    return `signature.${ext}`;
}

function loadCanvasImage(canvas) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = canvas.toDataURL('image/png');
    });
}

/** Find tight bounds around non-transparent pixels. */
function getContentBounds(ctx, width, height) {
    const { data } = ctx.getImageData(0, 0, width, height);
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let found = false;

    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const alpha = data[(y * width + x) * 4 + 3];
            if (alpha > 0) {
                found = true;
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
            }
        }
    }

    if (!found) return null;

    const pad = Math.max(8, Math.round(width * 0.02));
    const x = Math.max(0, minX - pad);
    const y = Math.max(0, minY - pad);
    return {
        x,
        y,
        width: Math.min(width - x, maxX - minX + 1 + pad * 2),
        height: Math.min(height - y, maxY - minY + 1 + pad * 2),
    };
}

/** Export the drawn signature as a trimmed PNG blob. */
export async function exportSignaturePng(sourceCanvas, { background = 'transparent', format = 'png' } = {}) {
    const bounds = getContentBounds(
        sourceCanvas.getContext('2d'),
        sourceCanvas.width,
        sourceCanvas.height,
    );
    if (!bounds) {
        throw new Error('empty');
    }

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = bounds.width;
    exportCanvas.height = bounds.height;
    const ctx = exportCanvas.getContext('2d');

    if (background === 'white') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, bounds.width, bounds.height);
    }

    const img = await loadCanvasImage(sourceCanvas);
    ctx.drawImage(
        img,
        bounds.x,
        bounds.y,
        bounds.width,
        bounds.height,
        0,
        0,
        bounds.width,
        bounds.height,
    );

    // JPG is laid onto white inside encodeCanvas; PNG and WebP keep the alpha.
    return (await encodeCanvas(exportCanvas, format)).blob;
}
